import React from 'react'
import ConnectedDevices from './ConnectedDevices'
import AppleIcon from '../../assets/Icons/Platforms/AppleLight.svg'
import AmazonIcon from '../../assets/Icons/Platforms/Amazon.svg'
import { BnSubpage } from '../Page'
import OpenAIIcon from '../../assets/Icons/Platforms/OpenAI.svg'
import OpenRouterIcon from '../../assets/Icons/Platforms/OpenRouter.svg'
import XAIIcon from '../../assets/Icons/Platforms/xI.svg'
import ClaudeIcon from '../../assets/Icons/Platforms/Claude.svg'
import GeminiIcon from '../../assets/Icons/Platforms/Gemini.svg'
import GitHubCopilotIcon from '../../assets/Icons/Platforms/GitHubCopilot.svg'
import KimiIcon from '../../assets/Icons/Platforms/Kimi.svg'
import HuggingFaceIcon from '../../assets/Icons/Platforms/HuggingFace.svg'
import PoeIcon from '../../assets/Icons/Platforms/Poe.svg'
import MistralIcon from '../../assets/Icons/Platforms/Mistral.svg'
import MiniMaxIcon from '../../assets/Icons/Platforms/MiniMax.svg'
import CursorIcon from '../../assets/Icons/Platforms/Cursor.svg'
import MetaIcon from '../../assets/Icons/Meta.svg'
import DownIcon from '../../assets/Icons/Down.svg'
import UpIcon from '../../assets/Icons/Up.svg'
import TickIcon from '../../assets/Icons/Tick.svg'
import PlayIcon from '../../assets/Icons/Play.svg'
import PauseIcon from '../../assets/Icons/Pause.svg'
import DisconnectCross from '../../assets/Icons/DisconnectCross.svg'
import IntelliKeyIcon from '../../assets/Icons/IntelliKeySml.svg'
import { intellikeyProvider } from '../../classes/InferenceProviderSelection'
import { hasNativeCapability } from '../../classes/NativeEnvironment'
import { isMobileBrowser, providerConnectionInstallPrompt } from './nativeConnection'
import modelIconMetrics from './modelIconMetrics.json'
import { skip } from 'rxjs/operators'
import { providerAvailabilityLabel, providerAvailabilityDetail } from '../../classes/ProviderAvailability'
import './index.css'

const providerIcon = provider => {
  if (provider.kind === 'device') return AppleIcon
  if (provider.id === 'intellikey') return IntelliKeyIcon
  if (provider.id === 'github-copilot') return GitHubCopilotIcon
  if (provider.id === 'kimi') return KimiIcon
  if (provider.id === 'huggingface') return HuggingFaceIcon
  if (provider.id === 'openai') return OpenAIIcon
  if (provider.id === 'grok') return XAIIcon
  if (provider.id === 'cursor') return CursorIcon
  if (provider.id === 'openrouter') return OpenRouterIcon
  if (provider.id === 'poe') return PoeIcon
  if (provider.id === 'mistral') return MistralIcon
  if (provider.id === 'minimax') return MiniMaxIcon
  if (provider.id === 'meta') return MetaIcon
  if (provider.id === 'aws-bedrock') return AmazonIcon
  if (provider.id === 'aws-anthropic') return ClaudeIcon
  return OpenRouterIcon
}

const modelIcon = (provider, model) => {
  const id = String((model && model.id) || '').toLowerCase()
  if (id.startsWith('google/') || id.includes('gemini')) return GeminiIcon
  if (id.startsWith('anthropic/') || id.includes('claude')) return ClaudeIcon
  if (id.startsWith('openai/') || id.startsWith('gpt-')) return OpenAIIcon
  if (id.startsWith('grok-')) return XAIIcon
  if (provider.id === 'cursor') return CursorIcon
  if (id.startsWith('kimi-') || id === 'k3') return KimiIcon
  if (id.startsWith('minimax-')) return MiniMaxIcon
  if (id.startsWith('mistral-') || id.startsWith('devstral-')) return MistralIcon
  return providerIcon(provider)
}

// SVG viewBoxes contain different amounts of whitespace. Normalize the visible
// artwork to a centred 20px extent without changing shared assets or row layout.
const modelIconGeometry = {
  [OpenAIIcon]: modelIconMetrics.OpenAI,
  [OpenRouterIcon]: modelIconMetrics.OpenRouter,
  [XAIIcon]: modelIconMetrics.xI,
  [ClaudeIcon]: modelIconMetrics.Claude,
  [GeminiIcon]: modelIconMetrics.Gemini,
  [GitHubCopilotIcon]: modelIconMetrics.GitHubCopilot,
  [KimiIcon]: modelIconMetrics.Kimi,
  [HuggingFaceIcon]: modelIconMetrics.HuggingFace,
  [PoeIcon]: modelIconMetrics.Poe,
  [MistralIcon]: modelIconMetrics.Mistral,
  [MiniMaxIcon]: modelIconMetrics.MiniMax,
  [CursorIcon]: modelIconMetrics.Cursor,
  [IntelliKeyIcon]: modelIconMetrics.IntelliKeySml
}

const modelIconStyle = icon => {
  const { scale, x, y } = modelIconGeometry[icon] || { scale: 1, x: 0, y: 0 }
  return { transform: `translate(${x}px, ${y}px) scale(${scale})` }
}

const errorMessage = error => {
  const response = error && error.response && error.response.data
  return (response && (response.message || response.error)) || (error && error.message) || String(error || '')
}

const byCombinedTokenPrice = (a, b) => {
  const cost = model => typeof model.inputPrice === 'number' && typeof model.outputPrice === 'number' &&
    Number.isFinite(model.inputPrice) && Number.isFinite(model.outputPrice)
    ? model.inputPrice + model.outputPrice : Infinity
  return cost(a) - cost(b) || String(a.name).localeCompare(String(b.name))
}

// These providers use device-held credentials, so the hosted catalog omits
// them. Keep them visible in the same subscription picker on every platform.
const awsProviders = [
  { id: 'aws-bedrock', name: 'Bedrock', group: 'aws', kind: 'device-credentials',
    oauthAvailable: false, defaultModel: '', dynamicModels: true, models: [] },
  { id: 'aws-anthropic', name: 'Anthropic', group: 'aws', kind: 'device-credentials',
    oauthAvailable: false, defaultModel: '', dynamicModels: true, models: [] }
]

export class SubscriptionProviders extends BnSubpage {
  constructor (props) {
    super(props)
    this.state = {
      providers: [],
      selectedProvider: 'intellikey',
      selectedModels: {},
      openProvider: null,
      loading: true,
      busyProvider: null,
      error: ''
    }
    this.catalogProviders = []
    this.serverStatus = null
    this.pollGeneration = 0
    this.pendingConnection = null
    this.pollInFlight = null
  }

  componentDidMount () {
    this.catalogUnsubscribe = this.props.me.observeSubscriptionCatalog(
      this.applyCatalog,
      error => this.setState({ loading: false, error: errorMessage(error) })
    )
    this.refresh()
    this.statusTimer = setInterval(this.refreshStatus, 15000)
    window.addEventListener('focus', this.refreshStatus)
    if (this.props.me.observeAvailableWords) {
      const uid = this.props.me.self?.uid
      // The observer starts with a placeholder zero, before Firestore answers.
      this.balanceSubscription = this.props.me.observeAvailableWords().pipe(skip(1)).subscribe(tokens => {
        if (!this.unmounted && this.props.me.self?.uid === uid) {
          this.tokenBalance = tokens
          this.setState({ providers: this.mergedProviders() })
        }
      })
    }
  }

  componentWillUnmount () {
    this.unmounted = true
    clearInterval(this.statusTimer)
    window.removeEventListener('focus', this.refreshStatus)
    this.balanceSubscription?.unsubscribe()
    this.stopPolling()
    if (this.catalogUnsubscribe) this.catalogUnsubscribe()
  }

  stopPolling = () => {
    this.pollGeneration = (this.pollGeneration || 0) + 1
    if (this.pollTimer) clearInterval(this.pollTimer)
    this.pollTimer = null
    this.pendingConnection = null
  }

  mergedProviders = () => {
    const statusProviders = Array.isArray(this.serverStatus && this.serverStatus.connections)
      ? this.serverStatus.connections
      : Array.isArray(this.serverStatus && this.serverStatus.providers)
        ? this.serverStatus.providers
        : []
    const statusById = Object.fromEntries(statusProviders.map(provider => [provider.id, provider]))
    // Native status owns its provider catalog even while token packs are active.
    const baseCatalog = Array.isArray(this.serverStatus?.catalog) ? this.serverStatus.catalog : this.catalogProviders
    const catalogWithDevices = [...baseCatalog, ...(this.serverStatus?.deviceCatalog || []).filter(device => !baseCatalog.some(provider => provider.id === device.id))]
    const catalog = [
      ...catalogWithDevices.map(provider => {
        const aws = awsProviders.find(candidate => candidate.id === provider.id)
        return aws ? { ...aws, ...provider, group: 'aws', kind: 'device-credentials' } : provider
      }),
      ...(!Array.isArray(this.serverStatus?.catalog)
        ? awsProviders.filter(aws => !catalogWithDevices.some(provider => provider.id === aws.id))
        : [])
    ]
    return [intellikeyProvider, ...catalog.filter(provider => provider.id !== 'intellikey')].map(provider => {
      const status = statusById[provider.id] || {}
      const dynamicModels = provider.dynamicModels
        ? (status.models || []).filter(model => !(provider.models || []).some(candidate => candidate.id === model.id))
        : []
      return {
        ...provider,
        connected: provider.id === 'intellikey' || Boolean(status.connected),
        status: status.status || 'disconnected',
        availability: provider.id === 'intellikey' && Number.isFinite(this.tokenBalance)
          ? this.tokenBalance <= 0 ? { kind: 'out-of-tokens' } : null
          : status.availability,
        pending: status.pending,
        connectionError: status.error,
        oauthAvailable: status.oauthAvailable === undefined ? provider.oauthAvailable : status.oauthAvailable,
        unavailableReason: status.unavailableReason || provider.unavailableReason,
        models: provider.group === 'aws'
          ? (status.connected ? [...(status.models || [])].sort(byCombinedTokenPrice) : [])
          : [...(provider.models || []), ...dynamicModels]
      }
    })
  }

  applyCatalog = providers => {
    this.catalogProviders = providers
    this.setState({
      providers: this.mergedProviders(),
      loading: false,
      error: ''
    })
  }

  applyStatus = status => {
    this.serverStatus = status || {}
    this.setState({
      providers: this.mergedProviders(),
      loading: Array.isArray(this.serverStatus.catalog) ? false : this.state.loading,
      selectedProvider: this.serverStatus.selectedProvider || 'intellikey',
      selectedModels: this.serverStatus.selectedModels || {},
      error: ''
    })
    return this.catalogProviders
  }

  refresh = async (isCurrent = () => true) => {
    const uid = this.props.me.self?.uid
    const generation = this.pollGeneration
    try {
      const status = await this.props.me.subscriptionProvidersStatus()
      if (!this.unmounted && this.props.me.self?.uid === uid && generation === this.pollGeneration && isCurrent()) this.applyStatus(status)
    } catch (error) {
      if (!this.unmounted && this.props.me.self?.uid === uid && generation === this.pollGeneration && isCurrent()) this.setState({ error: errorMessage(error) })
    }
  }

  refreshStatus = async () => {
    if (this.unmounted || this.statusInFlight || this.state.busyProvider || document.hidden) return
    this.statusInFlight = true
    try { await this.refresh(() => !this.state.busyProvider) } finally { this.statusInFlight = false }
  }

  toggleProvider = providerId => {
    this.setState({
      openProvider: this.state.openProvider === providerId ? null : providerId,
      error: ''
    })
  }

  nativeConnectionPrompt = () => providerConnectionInstallPrompt({
    isNative: this.props.me.isNative(),
    navigatorLike: typeof navigator === 'undefined' ? null : navigator,
    desktopReleases: this.props.desktopReleases
  })

  selectModel = async (provider, model) => {
    if (this.state.busyProvider) return
    if (!provider.connected) return this.connect(provider, model)
    this.stopPolling()
    this.setState({ busyProvider: provider.id, error: '' })
    try {
      const status = await this.props.me.selectSubscriptionProviderModel(
        provider.id,
        model.id,
        true
      )
      this.applyStatus(status)
    } catch (error) {
      this.setState({ error: errorMessage(error) })
    } finally {
      this.setState({ busyProvider: null })
    }
  }

  pollProvider = async providerId => {
    const pending = this.pendingConnection
    if (!pending || pending.providerId !== providerId || this.pollInFlight === pending) return
    const isCurrent = () => this.pendingConnection === pending &&
      pending.generation === this.pollGeneration && pending.uid === this.props.me.self?.uid
    if (!isCurrent()) return
    this.pollInFlight = pending
    try {
      const status = await this.props.me.pollSubscriptionProviderOAuth(providerId)
      if (!isCurrent()) return
      if (status.connected || status.status === 'expired' || status.status === 'missing' || status.status === 'disconnected' || status.status === 'error') {
        if (this.pollTimer) clearInterval(this.pollTimer)
        this.pollTimer = null
        this.setState({ busyProvider: providerId })
        // Show the new connection (and Disconnect) even if activating its
        // model subsequently fails. This refresh never chooses the model.
        await this.refresh(isCurrent)
        if (!isCurrent()) return
        if (status.connected) {
          // Keep the tapped model with this sign-in attempt. Catalog/status
          // refreshes must not replace it with the provider's default model.
          const selection = await this.props.me.selectSubscriptionProviderModel(providerId, pending.modelId, true)
          if (isCurrent()) this.applyStatus(selection)
        } else {
          if (isCurrent() && status.error) this.setState({ error: status.error })
        }
        if (isCurrent()) {
          this.stopPolling()
          this.setState({ busyProvider: null })
        }
      }
    } catch (error) {
      if (!isCurrent()) return
      this.stopPolling()
      this.setState({ busyProvider: null, error: errorMessage(error) })
    } finally {
      if (this.pollInFlight === pending) this.pollInFlight = null
    }
  }

  connect = async (provider, model) => {
    if (!provider.oauthAvailable || this.state.busyProvider) return
    const prompt = this.nativeConnectionPrompt()
    if (prompt) {
      this.setState({ openProvider: provider.id, error: prompt.message || 'Connect providers from the native IntelliKey app.' })
      if (prompt.url) this.props.me.openWindow(prompt.url, '_blank')
      return
    }
    this.stopPolling()
    const pending = {
      providerId: provider.id, modelId: model.id,
      generation: this.pollGeneration, uid: this.props.me.self?.uid
    }
    this.pendingConnection = pending
    const isCurrent = () => this.pendingConnection === pending &&
      pending.generation === this.pollGeneration && pending.uid === this.props.me.self?.uid
    this.setState({ busyProvider: provider.id, openProvider: provider.id, error: '' })
    try {
      await this.props.me.selectSubscriptionProviderModel(provider.id, model.id, false)
      if (!isCurrent()) return
      const info = await this.props.me.initiateSubscriptionProviderOAuth(provider.id)
      if (!isCurrent()) return
      const interval = Math.max(2, Number(info.intervalSeconds || 2)) * 1000
      this.pollTimer = setInterval(() => this.pollProvider(provider.id), interval)
      await this.refresh(isCurrent)
    } catch (error) {
      if (!isCurrent()) return
      this.stopPolling()
      this.setState({ busyProvider: null, error: errorMessage(error) })
    } finally {
      if (isCurrent()) this.setState({ busyProvider: null })
    }
  }

  connectAWS = async (provider) => {
    if (this.state.busyProvider) return
    const prompt = this.nativeConnectionPrompt()
    if (prompt) {
      this.setState({ openProvider: 'aws', error: prompt.message || 'Open IntelliKey on your device to connect AWS.' })
      return
    }
    const uid = this.props.me.self?.uid
    this.setState({ busyProvider: provider.id, openProvider: 'aws', error: '' })
    try {
      const connection = await this.props.me.connectAWSProvider(provider.id)
      if (this.unmounted || this.props.me.self?.uid !== uid || connection.cancelled) return
      await this.refresh()
    } catch (error) {
      if (!this.unmounted && this.props.me.self?.uid === uid) this.setState({ error: errorMessage(error) })
    } finally {
      if (!this.unmounted && this.props.me.self?.uid === uid) this.setState({ busyProvider: null })
    }
  }

  renderAWSGroup = providers => {
    const open = this.state.openProvider === 'aws'
    const selected = providers.some(provider => this.state.selectedProvider === provider.id)
    return <div key='aws' className={'subscriptionProvider' + (selected ? ' subscriptionProviderSelected' : '')}>
      <button type='button' className='subscriptionProviderHeader' aria-expanded={open} onClick={() => this.toggleProvider('aws')}>
        <div className='subscriptionProviderIdentity'>
          <span className='subscriptionProviderIconSlot'><img className='subscriptionProviderIcon' src={AmazonIcon} alt='' aria-hidden='true' /></span>
          <span className='subscriptionProviderText'>
            <span className='subscriptionProviderName'>AWS</span>
            <span className='subscriptionProviderNote'>API Keys</span>
          </span>
        </div>
        <span className='subscriptionDisclosureSlot'>
          <img className='subscriptionDisclosure' src={open ? UpIcon : DownIcon} alt='' aria-hidden='true' />
        </span>
      </button>
      {open && <div className='subscriptionProviderBody'>
        <div className='subscriptionModels' role='tree'>
          {providers.map(provider => {
            const active = this.state.selectedProvider === provider.id && provider.connected
            const icon = providerIcon(provider)
            const availabilityLabel = providerAvailabilityLabel(provider.availability)
            const connectionNotice = provider.status === 'error'
              ? (provider.connectionError || 'Reconnect to AWS.')
              : availabilityLabel
            return <div className='subscriptionAWSRow' key={provider.id}>
              <div className='subscriptionAWSConnection'>
                <button type='button' role='treeitem' aria-selected={active}
                  className={'subscriptionModel' + (active ? ' subscriptionModelSelected' : '')}
                  disabled={Boolean(this.state.busyProvider)}
                  onClick={() => this.connectAWS(provider)}>
                  <span className='subscriptionModelIconSlot'>
                    <img className='subscriptionModelIcon' src={icon} style={modelIconStyle(icon)} alt='' aria-hidden='true' />
                  </span>
                  <span className='subscriptionModelText'>
                    <span className='subscriptionModelName'>{provider.name}</span>
                    <span className='subscriptionModelNote' role={connectionNotice ? 'status' : undefined}>
                      {connectionNotice || (provider.connected ? 'Connected · Change API key' : 'Connect with API key')}
                    </span>
                  </span>
                  <span className='subscriptionModelState'>
                    {active && <img className='subscriptionSelectedIcon' src={TickIcon} alt='Selected' />}
                  </span>
                </button>
                {provider.connected && <button type='button' className='subscriptionAWSDisconnect'
                  aria-label={`Disconnect ${provider.name} on AWS`}
                  disabled={Boolean(this.state.busyProvider)}
                  onClick={event => this.disconnect(event, provider)}>
                  <img className='subscriptionDisconnectIcon' src={DisconnectCross} alt='' aria-hidden='true' />
                </button>}
              </div>
              {provider.connected && <div className='subscriptionAWSModels'>
                {provider.models.length === 0
                  ? <div className='subscriptionProviderUnavailable'>Reconnect to load available models and prices.</div>
                  : provider.models.map(model => this.renderModel(provider, model))}
              </div>}
            </div>
          })}
        </div>
        <div className='subscriptionProviderInstructions'>Prices are USD per 1M input/output tokens. Published standard rates; your account’s charges may differ.</div>
      </div>}
    </div>
  }

  disconnect = async (event, provider) => {
    event.stopPropagation()
    if (this.state.busyProvider) return
    this.stopPolling()
    this.setState({ busyProvider: provider.id, error: '' })
    try {
      await this.props.me.disconnectSubscriptionProvider(provider.id)
      await this.refresh()
    } catch (error) {
      this.setState({ error: errorMessage(error) })
    } finally {
      this.setState({ busyProvider: null })
    }
  }

  renderModel = (provider, model) => {
    const icon = modelIcon(provider, model)
    const providerModel = this.state.selectedModels[provider.id] || provider.defaultModel
    const selected = this.state.selectedProvider === provider.id && providerModel === model.id
    const active = selected && provider.connected
    const showDefault = model.recommended && !selected
    const unavailable = !provider.connected && !provider.oauthAvailable
    const localStopped = provider.local && provider.runningModel !== model.id
    const selection = (
      <button
        type='button'
        key={model.id}
        role='treeitem'
        aria-selected={selected}
        disabled={Boolean(this.state.busyProvider) || unavailable || localStopped}
        className={'subscriptionModel' + (selected ? ' subscriptionModelSelected' : '') + (active ? ' subscriptionModelActive' : '') + (unavailable ? ' subscriptionModelUnavailable' : '')}
        onClick={() => this.selectModel(provider, model)}
      >
        <span className='subscriptionModelIconSlot'>
          <img className='subscriptionModelIcon' src={icon} style={modelIconStyle(icon)} alt='' aria-hidden='true' />
        </span>
        <span className='subscriptionModelText'>
          <span className='subscriptionModelName'>{model.name}</span>
          <span className='subscriptionModelNote'>{provider.local && provider.starting ? provider.phase || 'Starting…' : model.note}</span>
        </span>
        <span className={'subscriptionModelState' + (showDefault ? ' subscriptionModelStateDefault' : '')}>
          {showDefault && <span className='subscriptionDefaultLabel'>Default</span>}
          {selected && <img className='subscriptionSelectedIcon' src={TickIcon} alt='Selected' />}
        </span>
      </button>
    )
    if (!provider.local) return selection
    const playing = provider.runningModel === model.id
    const runLabel = provider.starting ? 'Cancel' : playing ? 'Pause' : 'Play'
    return <div key={model.id} className='subscriptionLocalModelRow'>
      {selection}
      <button type='button' className={'subscriptionModelRunAction' + (selected ? ' subscriptionModelSelected' : '')}
        aria-label={`${runLabel} ${model.name}`}
        disabled={Boolean(this.state.busyProvider)}
        onClick={() => this.toggleLocalModel(provider, model)}>
        <img src={playing || provider.starting ? PauseIcon : PlayIcon} alt='' aria-hidden='true' />
      </button>
    </div>
  }

  toggleLocalModel = async (provider, model) => {
    if (this.state.busyProvider) return
    const uid = this.props.me.self?.uid
    this.setState({ busyProvider: provider.id, error: '' })
    try {
      await this.props.me.deviceNetwork('serving', { enabled: !provider.starting && provider.runningModel !== model.id, model: model.id })
      if (this.props.me.self?.uid === uid) await this.refresh()
    } catch (error) {
      if (this.props.me.self?.uid === uid) this.setState({ error: errorMessage(error) })
    } finally {
      if (this.props.me.self?.uid === uid) this.setState({ busyProvider: null })
    }
  }

  renderProvider = provider => {
    const open = this.state.openProvider === provider.id
    const pending = provider.status === 'pending'
    const selected = this.state.selectedProvider === provider.id
    const device = provider.kind === 'device'
    const active = provider.connected && selected && (!device || Boolean(provider.runningModel))
    const connectionLabel = device
      ? provider.starting ? 'Starting…' : provider.runningModel ? active ? 'In use' : 'Running' : 'Stopped'
      : active ? 'In use' : provider.id === 'intellikey' ? 'Available' : 'Connected'
    const actionLabel = provider.connected ? `Disconnect ${provider.name}` : 'Cancel connection'
    const managed = provider.id === 'intellikey'
    const showConnectionAction = !managed && provider.kind !== 'device' && (provider.connected || pending)
    const actionDisabled = Boolean(this.state.busyProvider)
    const availabilityLabel = providerAvailabilityLabel(provider.availability)
    return (
      <div key={provider.id} className={'subscriptionProvider' + (selected ? ' subscriptionProviderSelected' : '') + (active ? ' subscriptionProviderActive' : '')}>
        <button type='button' className='subscriptionProviderHeader' aria-expanded={open} onClick={() => this.toggleProvider(provider.id)}>
          <div className='subscriptionProviderIdentity'>
            <span className='subscriptionProviderIconSlot'>
              <img className='subscriptionProviderIcon' src={providerIcon(provider)} alt='' aria-hidden='true' />
            </span>
            <span className='subscriptionProviderText'>
              <span className='subscriptionProviderName'>{provider.name}</span>
              <span className='subscriptionProviderNote'>{managed ? 'Token Packs' : provider.kind === 'device' ? 'Local inference' : provider.connected ? 'Connected' : 'Login'}</span>
            </span>
          </div>
          <div className='subscriptionProviderControls'>
            {availabilityLabel ? <span role='status' className='subscriptionProviderConnection subscriptionProviderLimit'>{availabilityLabel}</span>
              : provider.connected && <span className='subscriptionProviderConnection'>{connectionLabel}</span>}
            {pending && <span className='subscriptionProviderConnection'>Connecting…</span>}
            <span className='subscriptionDisclosureSlot'>
              <img className='subscriptionDisclosure' src={open ? UpIcon : DownIcon} alt='' aria-hidden='true' />
            </span>
          </div>
        </button>
        {open && (
          <div className='subscriptionProviderBody'>
            {availabilityLabel && <div role='status' className='subscriptionError'>{providerAvailabilityDetail(provider.availability)}</div>}
            {showConnectionAction && <button
              type='button'
              disabled={actionDisabled}
              className={'subscriptionConnectionAction' + (provider.connected ? ' subscriptionConnectionDisconnect' : '')}
              onClick={event => this.disconnect(event, provider)}
            >{provider.connected ? <>
                <span className='subscriptionConnectionIconSlot'>
                  <img className='subscriptionDisconnectIcon' src={DisconnectCross} alt='' aria-hidden='true' />
                </span>
                <span className='subscriptionConnectionLabel'>{actionLabel}</span>
              </> : actionLabel}
            </button>}
            {!managed && !provider.oauthAvailable && provider.unavailableReason && <div className='subscriptionProviderUnavailable'>{provider.unavailableReason}</div>}
            {pending && provider.pending?.userCode && <div className='subscriptionProviderInstructions'>
              Enter this code on the provider’s sign-in page: <strong>{provider.pending.userCode}</strong>
            </div>}
            <div className='subscriptionModels' role='tree'>
              {provider.models.map(model => this.renderModel(provider, model))}
            </div>
          </div>
        )}
      </div>
    )
  }

  renderContent () {
    const desktopWebApp = !this.props.me.isNative() &&
      !isMobileBrowser(typeof navigator === 'undefined' ? null : navigator)
    const directProvider = this.props.me.isNative() && this.serverStatus?.privateMode &&
      this.state.selectedProvider !== 'intellikey'
    const deviceTokenPacks = this.props.me.isNative() && hasNativeCapability('tokenPackDeviceRuntime') &&
      this.state.selectedProvider === 'intellikey'
    return (
      <div className='bnSubpageContent subscriptionProvidersPage'>
        <div className='subscriptionIntro'>
          {desktopWebApp ? 'Choose AI Provider from your iPhone' : 'Choose AI Provider'}
        </div>
        {this.state.error && <div className='subscriptionError'>{this.state.error}</div>}
        {this.state.loading
          ? <div className='subscriptionLoading'>Loading subscriptions…</div>
          : <div className='subscriptionProviderTree'>
              {this.state.providers.filter(provider => provider.group !== 'aws').map(this.renderProvider)}
              {this.state.providers.some(provider => provider.group === 'aws') &&
                this.renderAWSGroup(this.state.providers.filter(provider => provider.group === 'aws'))}
            </div>}
        <div className='subscriptionFootnote'>
          {deviceTokenPacks
            ? 'No data is stored on IntelliKey servers. All user data is directly forwarded to AI Models hosted by Google Cloud and/or Amazon Bedrock.'
            : directProvider
            ? 'AI requests may include the text you select and nearby text needed for context. Requests go directly from this device to the provider you choose. Your credentials and request data are not sent to IntelliKey servers. Data retention depends on the provider.'
            : 'AI requests may include the text you select and nearby text needed for context. Requests are sent to the provider you choose and handled under its privacy policy. Data retention depends on the provider.'}
        </div>
        <ConnectedDevices me={this.props.me} onChange={this.refresh} providers={this.state.providers} />
      </div>
    )
  }
}
